import React from "react";
import * as FileSaver from "file-saver";
import XLSX from "sheetjs-style-v2";
import Button from "components/Button";
import LoadingDots from "components/LoadingDots";

const formatTimeOnly = (start) => {
    let date;
    if (start && typeof start === "object" && "_seconds" in start) {
        date = new Date(start._seconds * 1000);
    } else if (typeof start === "number") {
        date = new Date(start * 1000);
    } else {
        date = new Date(start);
    }
    if (!date || isNaN(date.getTime())) return "n/a";

    const datePart = date.toLocaleDateString([], { month: "short", day: "numeric" });
    const timePart = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
    return `${datePart} ${timePart}`;
};

export default React.memo(
    ({
        hourlyDataset = [],
        binnedDataset = [],
        fileName,
        hasTemperatureData,
        exporting,
        exportingCsv,
        toggleExporting,
        toggleCsvExporting,
        toggleErrorMsg,
    }) => {
        const fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        const fileExtension = ".xlsx";

        const exportToExcel = async () => {
            toggleExporting(true);
            toggleCsvExporting(true);

            await new Promise((resolve) => setTimeout(resolve, 100));

            try {
                let temperatureDataRows = [];
                let timeDataRows = [];
                let boxPlotDataRows = [];

                if (hasTemperatureData) {
                    temperatureDataRows = hourlyDataset.map((row) => ({
                        "Start Time": formatTimeOnly(row.start),
                        Value: row.value,
                        "Temperature (°C)": row.temp,
                    }));
                    timeDataRows = hourlyDataset.map((row) => ({
                        "Start Time": formatTimeOnly(row.start),
                        Value: row.value,
                    }));
                } else {
                    timeDataRows = hourlyDataset.map((row) => ({
                        "Start Time": formatTimeOnly(row.start),
                        Value: row.value,
                    }));
                } // else, do nothing

                if (binnedDataset.length > 0) {
                    boxPlotDataRows = binnedDataset.map((bin) => {
                        const stats = bin.combinedBoxplotParams || {}; // TODO: OLIVIA - CHANGE THIS TO THE CORRECT PARAMS
                        return {
                            "Bin Center (°C)": bin.binCenter,
                            Min: stats.min || "n/a",
                            Q1: stats.q1,
                            Median: stats.median,
                            Q3: stats.q3,
                            Max: stats.max || "n/a",
                            Mean: stats.mean,
                            "Standard Deviance": stats.stdDev,
                            Variance: stats.variance,
                            "Interquartile Range (IQR)": stats.iqr,
                        };
                    });
                }

                // const temperatureSheet = XLSX.utils.json_to_sheet(temperatureDataRows);
                // const timeSheet = XLSX.utils.json_to_sheet(timeDataRows);
                // const boxPlotSheet = XLSX.utils.json_to_sheet(boxPlotDataRows);

                // const workbook = {
                //     Sheets: {
                //         "Temperature Scatter Plot Data": temperatureSheet,
                //         "Time Scatter Plot Data": timeSheet,
                //         "Box Plot Data": boxPlotSheet,
                //     },
                //     SheetNames: [
                //         "Temperature Scatter Plot Data",
                //         "Time Scatter Plot Data",
                //         "Box Plot Data",
                //     ],
                // };

                const workbook = {
                    Sheets: {},
                    SheetNames: [],
                };

                if (temperatureDataRows.length > 0) {
                    const temperatureSheet = XLSX.utils.json_to_sheet(temperatureDataRows);
                    workbook.Sheets["Temperature Scatter Plot Data"] = temperatureSheet;
                    workbook.SheetNames.push("Temperature Scatter Plot Data");
                }

                if (timeDataRows.length > 0) {
                    const timeSheet = XLSX.utils.json_to_sheet(timeDataRows);
                    workbook.Sheets["Time Scatter Plot Data"] = timeSheet;
                    workbook.SheetNames.push("Time Scatter Plot Data");
                }

                if (boxPlotDataRows.length > 0) {
                    const boxPlotSheet = XLSX.utils.json_to_sheet(boxPlotDataRows);
                    workbook.Sheets["Box Plot Data"] = boxPlotSheet;
                    workbook.SheetNames.push("Box Plot Data");
                }

                if (workbook.SheetNames.length === 0) {
                    toggleErrorMsg("No data available to export.");
                    toggleExporting(false);
                    toggleCsvExporting(false);
                    return;
                }

                const excelBuffer = XLSX.write(workbook, {
                    bookType: "xlsx",
                    type: "array",
                });

                const data = new Blob([excelBuffer], { type: fileType });
                const fileNameWithoutExtension = fileName.split(".")[0];
                FileSaver.saveAs(data, fileNameWithoutExtension + fileExtension);
                toggleErrorMsg("");
            } catch (err) {
                console.warn(err);
                toggleErrorMsg("An error occurred while trying to export your CSV.");
                toggleExporting(false);
                toggleCsvExporting(false);
            }

            toggleExporting(false);
            toggleCsvExporting(false);
        };

        return (
            <Button
                size="medium"
                variant="contained"
                onClick={() => exportToExcel()}
                color="primary"
                style={{ cursor: "pointer", fontSize: 14 }}
                disabled={exporting || exportingCsv}
            >
                {exporting || exportingCsv ? <LoadingDots /> : "Export CSV"}
            </Button>
        );
    }
);
